import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

axios.defaults.withCredentials = true;

// Global handler: surface trial_expired (402) errors uniformly
let _trialExpiredToastShown = 0;
axios.interceptors.response.use(
  (r) => r,
  (error) => {
    const status = error?.response?.status;
    const code = error?.response?.data?.code;
    if (status === 402 && code === 'trial_expired') {
      const now = Date.now();
      if (now - _trialExpiredToastShown > 4000) {
        _trialExpiredToastShown = now;
        toast.error('Seu trial expirou. Assine um plano para continuar editando.', {
          action: { label: 'Ver planos', onClick: () => { window.location.href = '/billing'; } },
        });
      }
    }
    return Promise.reject(error);
  }
);

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

function formatApiErrorDetail(detail) {
  if (detail == null) return 'Algo deu errado. Tente novamente.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [billing, setBilling] = useState(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const refreshBilling = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/billing/status`, { withCredentials: true });
      setBilling({
        plan: data.plan,
        subscription_status: data.subscription_status,
        trial_ends_at: data.trial_ends_at,
        days_left: data.days_left,
        blocked: !!data.blocked,
        is_trial: !!data.plan?.is_trial,
      });
    } catch (e) {
      setBilling(null);
    }
  }, [BACKEND_URL]);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        withCredentials: true,
      });
      setUser(data);
      if (data && data.role !== 'super_admin' && data.tenant_id) {
        refreshBilling();
      } else if (data && data.role === 'super_admin' && data.is_impersonating) {
        refreshBilling();
      } else {
        setBilling(null);
      }
    } catch (error) {
      setUser(false);
      setBilling(null);
    } finally {
      setLoading(false);
    }
  }, [BACKEND_URL, refreshBilling]);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      await checkAuth();
      return { success: true, user: data };
    } catch (error) {
      const message = formatApiErrorDetail(error.response?.data?.detail) || error.message;
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
      setUser(false);
      setBilling(null);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const enterTenant = async (tenantId) => {
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/platform/tenants/${tenantId}/enter`,
        {},
        { withCredentials: true }
      );
      await checkAuth();
      return { success: true, data };
    } catch (error) {
      return { success: false, error: formatApiErrorDetail(error.response?.data?.detail) };
    }
  };

  const exitTenant = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/platform/exit`, {}, { withCredentials: true });
      await checkAuth();
      return { success: true };
    } catch (error) {
      return { success: false, error: formatApiErrorDetail(error.response?.data?.detail) };
    }
  };

  const isSuperAdmin = user && user.role === 'super_admin';
  const isImpersonating = user && user.is_impersonating;
  const activeTenant = user && user.active_tenant;
  const isReadOnly = !!(billing && billing.blocked && !isSuperAdmin);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        logout,
        checkAuth,
        enterTenant,
        exitTenant,
        isSuperAdmin,
        isImpersonating,
        activeTenant,
        billing,
        refreshBilling,
        isReadOnly,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
